import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from "@mui/material";

const BatchReportModal = ({ open, onClose, batchName, report }) => {
  if (!report || report.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{batchName} - Import Report</DialogTitle>
        <DialogContent>
          <Typography>No report data available.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Calculate summary statistics
  const totalRows = report.length;
  const successRows = report.filter((r) => r.status === "success").length;
  const failedRows = report.filter((r) => r.status === "fail").length;
  const skippedRows = report.filter((r) => r.status === "skip").length;

  // "Secret" Created By column: only show if the backend included it in the report.
  // (Backend only includes this when CSV has Creator/createdBy column.)
  const showCreatedByColumn = report.some(
    (r) => Object.prototype.hasOwnProperty.call(r || {}, "createdBy")
  );

  // Helper function to get color for status
  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "fail":
        return "error";
      case "skip":
        return "warning";
      case "none":
        return "default";
      default:
        return "default";
    }
  };

  // Helper function to format status text
  const formatStatus = (status) => {
    if (!status) return "none";
    return status.replace(/_/g, " ");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{batchName} - Import Report</DialogTitle>
      <DialogContent>
        {/* Summary Statistics */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip label={`Total: ${totalRows}`} color="primary" />
            <Chip label={`Success: ${successRows}`} color="success" />
            <Chip label={`Failed: ${failedRows}`} color="error" />
            <Chip label={`Skipped: ${skippedRows}`} color="warning" />
          </Box>
        </Box>

        {/* Detailed Report Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Row 1
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Photo
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Audio
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Told By
                </TableCell>
                {showCreatedByColumn && (
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Created By
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                  Error
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.map((row) => (
                <TableRow
                  key={row.rowNumber}
                  sx={{
                    "&:nth-of-type(odd)": { bgcolor: "#fafafa" },
                    "&:hover": { bgcolor: "#f0f0f0" },
                  }}
                >
                  <TableCell>{row.rowNumber}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(row.status)}
                      color={getStatusColor(row.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(row.photo)}
                      color={getStatusColor(row.photo)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(row.audio)}
                      color={getStatusColor(row.audio)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(row.title)}
                      color={getStatusColor(row.title)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {formatStatus(row.toldBy)}
                    </Typography>
                  </TableCell>
                  {showCreatedByColumn && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {formatStatus(row.createdBy)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: row.error ? "error.main" : "text.secondary",
                        maxWidth: 520,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                      title={row.error || ""}
                    >
                      {row.error || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchReportModal;
